import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  PlusIcon, PencilSquareIcon, TrashIcon, CheckIcon,
  ChatBubbleLeftEllipsisIcon, ArrowPathIcon, ChartBarIcon, StarIcon,
} from '@heroicons/react/24/outline'
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid'
import api from '../../services/api'
import RichTextEditor, { getRichTextPlainText, getRichTextWordCount } from '../../components/common/RichTextEditor'

// ─── Types ────────────────────────────────────────────────────────────────────

type BillingPeriod = 'monthly' | 'quarterly' | 'yearly'

interface Plan {
  id: number
  slug: string
  name: string
  description?: string
  price_min: number
  price_max: number
  duration_days: number
  plan_type: string
  billing_period: BillingPeriod
  discount_percent: number
  final_price: number
  is_popular: boolean
  is_active: boolean
  highlight_badge?: string | null
  target_audience?: string | null
  plan_highlights?: string | null
  sort_order?: number
  core_details?: string
  email_delivery: boolean
  telegram_delivery: boolean
  whatsapp_support: boolean
  priority_support: boolean
  monthly_outlook: boolean
  weekly_sectoral_reports: boolean
  custom_research_reports: boolean
  market_radar_newsletter: boolean
  intraday_trades: boolean
  positional_trades: boolean
  strategy_based_options: boolean
  portfolio_tracker?: string
  portfolio_audit_frequency?: string
  portfolio_rebalancing: boolean
  risk_management_plan: boolean
  watchlist_access: boolean
  alerts_access: boolean
  risk_meter_access: boolean
  live_market_access: boolean
  review_calls?: string
  call_support: boolean
  compliance_dashboard: boolean
  net_worth_certificate: boolean
  basic_education_content: boolean
  active_subscribers: number
}

interface FAQ {
  id: number
  question: string
  answer: string
  sort_order: number
  is_active: boolean
}

const BILLING_SECTIONS: { key: BillingPeriod; label: string }[] = [
  { key: 'monthly', label: 'Monthly' },
  { key: 'quarterly', label: 'Quarterly' },
  { key: 'yearly', label: 'Yearly' },
]

// ─── Plan Form ─────────────────────────────────────────────────────────────────

const EMPTY_PLAN: Omit<Plan, 'id' | 'active_subscribers' | 'final_price'> = {
  name: '', slug: '', description: '', price_min: 0, price_max: 0, duration_days: 30,
  plan_type: 'BASIC', billing_period: 'monthly', discount_percent: 0,
  is_popular: false, is_active: true,
  target_audience: '', plan_highlights: '',
  highlight_badge: '', sort_order: 0,
  core_details: undefined,
  email_delivery: true, telegram_delivery: false, whatsapp_support: false, priority_support: false,
  monthly_outlook: false, weekly_sectoral_reports: false, custom_research_reports: false, market_radar_newsletter: false,
  intraday_trades: false, positional_trades: false, strategy_based_options: false,
  portfolio_tracker: '', portfolio_audit_frequency: '', portfolio_rebalancing: false, risk_management_plan: false,
  watchlist_access: false, alerts_access: false, risk_meter_access: false, live_market_access: false,
  review_calls: '', call_support: false,
  compliance_dashboard: false, net_worth_certificate: false, basic_education_content: true,
}

function PlanForm({ initial, onSave, onCancel }: {
  initial?: Partial<Plan>
  onSave: (data: any) => Promise<void>
  onCancel: () => void
}) {
  const [form, setForm] = useState({ ...EMPTY_PLAN, ...initial })
  const [highlights, setHighlights] = useState<string[]>(() => {
    try {
      if (initial?.plan_highlights) {
        const parsed = JSON.parse(initial.plan_highlights)
        if (Array.isArray(parsed)) return parsed
      }
    } catch {}
    return initial?.plan_highlights ? [initial.plan_highlights] : []
  })
  const [coreDetails, setCoreDetails] = useState<{title: string, value: string}[]>(() => {
    try {
      if (initial?.core_details) {
        const parsed = JSON.parse(initial.core_details)
        if (Array.isArray(parsed)) return parsed
      }
    } catch {}
    return []
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleAddHighlight = () => setHighlights(p => [...p, ''])
  const handleRemoveHighlight = (i: number) => setHighlights(p => p.filter((_, idx) => idx !== i))
  const handleHighlightChange = (i: number, val: string) => {
    const next = [...highlights]
    next[i] = val
    setHighlights(next)
  }

  const handleAddCoreDetail = () => setCoreDetails(p => [...p, {title: '', value: ''}])
  const handleRemoveCoreDetail = (i: number) => setCoreDetails(p => p.filter((_, idx) => idx !== i))
  const handleCoreDetailChange = (i: number, field: 'title' | 'value', val: string) => {
    const next = [...coreDetails]
    next[i][field] = val
    setCoreDetails(next)
  }
  const handleMoveCoreDetail = (i: number, dir: 1 | -1) => {
    if (i + dir < 0 || i + dir >= coreDetails.length) return
    const next = [...coreDetails]
    const temp = next[i]
    next[i] = next[i + dir]
    next[i + dir] = temp
    setCoreDetails(next)
  }

  const set = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm(p => ({ ...p, [field]: e.target.value }))
  const toggle = (field: string) => () =>
    setForm(p => ({ ...p, [field]: !(p as any)[field] }))

  // Mirrors the server's pricing rule so the admin sees what customers will pay.
  const listPrice = Number(form.price_min) || 0
  const discountPct = Math.min(Math.max(Number(form.discount_percent) || 0, 0), 100)
  const payable = Math.round(listPrice * (100 - discountPct)) / 100

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name || !form.price_min) { setError('Name and list price are required'); return }
    if (getRichTextWordCount(form.description || '') > 200) { setError('Description must be 200 words or fewer'); return }
    const priceMin = Number(form.price_min)
    const priceMax = Number(form.price_max) || priceMin
    const durationDays = Number(form.duration_days)
    const discountPercent = Number(form.discount_percent) || 0
    if (!(priceMin > 0)) { setError('List price must be greater than 0'); return }
    if (priceMax < priceMin) { setError('Maximum price cannot be less than list price'); return }
    if (!(durationDays > 0)) { setError('Duration must be greater than 0 days'); return }
    if (discountPercent < 0 || discountPercent > 100) { setError('Discount must be between 0 and 100'); return }
    setLoading(true)
    setError('')
    try {
      await onSave({
        ...form,
        price_min: priceMin,
        price_max: priceMax,
        duration_days: durationDays,
        discount_percent: discountPercent,
        sort_order: Number(form.sort_order) || 0,
        highlight_badge: form.highlight_badge?.trim() || null,
        target_audience: form.target_audience?.trim() || null,
        plan_highlights: highlights.map(h => h.trim()).filter(Boolean).length > 0 ? JSON.stringify(highlights.map(h => h.trim()).filter(Boolean)) : null,
        core_details: coreDetails.filter(d => d.title.trim() && d.value.trim()).length > 0 ? JSON.stringify(coreDetails.filter(d => d.title.trim() && d.value.trim())) : null,
        portfolio_tracker: form.portfolio_tracker || null,
        portfolio_audit_frequency: form.portfolio_audit_frequency || null,
        review_calls: form.review_calls || null,
      })
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Save failed')
    } finally {
      setLoading(false)
    }
  }

  const cls = "w-full text-sm bg-dark-900 border border-dark-600 text-white placeholder-gray-500 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
  const sh = "text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3"

  // Render this as a helper, not as a nested React component. A component
  // declared inside PlanForm gets a new identity after every state update,
  // causing React to remount its input and drop focus on each keystroke.
  const renderField = ({ label, field, type = 'text', placeholder = '' }: { label: string; field: string; type?: string; placeholder?: string }) => (
    <div>
      <label className="block text-xs font-medium text-gray-400 mb-1">{label}</label>
      <input type={type} value={(form as any)[field] ?? ''} onChange={set(field)} placeholder={placeholder} className={cls} />
    </div>
  )

  const Toggle = ({ label, field }: { label: string; field: string }) => (
    <label className="flex items-center gap-2 cursor-pointer">
      <div onClick={toggle(field)} className={`relative flex-shrink-0 rounded-full transition-colors ${(form as any)[field] ? 'bg-amber-500' : 'bg-dark-600'}`} style={{ width: 32, height: 18 }}>
        <span className="absolute rounded-full bg-white shadow transition-all" style={{ width: 14, height: 14, top: 2, left: (form as any)[field] ? 16 : 2 }} />
      </div>
      <span className="text-xs text-gray-300">{label}</span>
    </label>
  )

  return (
    <form onSubmit={submit} className="space-y-6">
      {error && <p className="text-sm text-red-400">{error}</p>}

      {/* Basic */}
      <div>
        <p className={sh}>Basic Info</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {renderField({ label: 'Plan Name *', field: 'name', placeholder: 'e.g. Premium Plan' })}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Plan Type</label>
            <select value={form.plan_type} onChange={set('plan_type')} className={cls}>
              {['BASIC', 'STANDARD', 'PREMIUM', 'VIP'].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Billing Period</label>
            <select value={form.billing_period} onChange={set('billing_period')} className={cls}>
              {BILLING_SECTIONS.map(b => <option key={b.key} value={b.key}>{b.label}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          {renderField({ label: 'Target Audience', field: 'target_audience', placeholder: 'e.g. Students, new investors...' })}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-2">Plan Highlights</label>
            <div className="space-y-2">
              {highlights.map((highlight, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={highlight}
                    onChange={(e) => handleHighlightChange(index, e.target.value)}
                    placeholder="e.g. Perfect for beginners"
                    className={`${cls} flex-1`}
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveHighlight(index)}
                    className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors flex-shrink-0"
                    title="Remove Highlight"
                  >
                    <TrashIcon style={{ width: 16, height: 16 }} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={handleAddHighlight}
                className="text-xs flex items-center gap-1 font-medium text-primary-400 hover:text-primary-300 transition-colors mt-2"
              >
                <PlusIcon style={{ width: 12, height: 12 }} /> Add Highlight
              </button>
            </div>
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-xs font-medium text-gray-400 mb-1">Description (Internal / Legacy)</label>
          <RichTextEditor
            value={form.description ?? ''}
            onChange={(description) => setForm(p => ({ ...p, description }))}
            placeholder="Describe what this plan includes…"
            maxWords={200}
            className="bg-dark-900"
          />
        </div>
      </div>

      {/* Display */}
      <div className="border-t border-dark-700 pt-5">
        <p className={sh}>Display & Ordering</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {renderField({ label: 'Highlight Badge', field: 'highlight_badge', placeholder: 'e.g. Most Popular, Best Value' })}
          {renderField({ label: 'Sort Order', field: 'sort_order', type: 'number', placeholder: '0 = first' })}
        </div>
        <div className="mt-4">
          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <div
              onClick={toggle('is_popular')}
              className={`flex-shrink-0 flex items-center justify-center rounded-md border transition-colors ${form.is_popular ? 'bg-amber-500 border-amber-500' : 'bg-dark-900 border-dark-500'}`}
              style={{ width: 20, height: 20 }}
            >
              {form.is_popular && <CheckIcon className="text-black" style={{ width: 14, height: 14, strokeWidth: 3 }} />}
            </div>
            <span className="text-xs text-gray-300 flex items-center gap-1">
              <StarIcon style={{ width: 13, height: 13 }} /> Mark as "Most Popular"
            </span>
          </label>
          <p className="text-xs text-gray-600 mt-1.5 ml-[30px]">
            One plan per billing period (Monthly, Quarterly, Yearly) can be popular.
            Selecting this will automatically unmark whichever plan in the same period is currently popular.
          </p>
        </div>
      </div>

      {/* Pricing */}
      <div className="border-t border-dark-700 pt-5">
        <p className={sh}>Pricing</p>
        <div className="grid grid-cols-3 gap-4">
          {renderField({ label: 'List Price (₹) *', field: 'price_min', type: 'number', placeholder: '4999' })}
          {renderField({ label: 'Max Price (₹)', field: 'price_max', type: 'number', placeholder: '4999' })}
          {renderField({ label: 'Duration (days)', field: 'duration_days', type: 'number', placeholder: '90' })}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          {renderField({ label: 'Discount (% off)', field: 'discount_percent', type: 'number', placeholder: '0' })}
          <div className="flex items-end">
            <p className="text-xs text-gray-400 pb-2">
              {discountPct > 0 ? (
                <>
                  Customers pay{' '}
                  <span className="font-semibold text-amber-400">
                    ₹{payable.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </span>{' '}
                  per {BILLING_SECTIONS.find(b => b.key === form.billing_period)?.label.toLowerCase() ?? 'period'}
                  {' '}— <span className="line-through">₹{listPrice.toLocaleString('en-IN')}</span>,
                  saving ₹{(listPrice - payable).toLocaleString('en-IN', { maximumFractionDigits: 2 })}.
                </>
              ) : (
                <>No discount — customers pay the list price. The pricing page shows no "% off" badge.</>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Core */}
      <div className="border-t border-dark-700 pt-5">
        <p className={sh}>Core Details</p>
        <div className="space-y-3">
          {coreDetails.map((detail, index) => (
            <div key={index} className="flex gap-2 items-start">
              <input type="text" value={detail.title} onChange={(e) => handleCoreDetailChange(index, 'title', e.target.value)} placeholder="Title (e.g. Recommendations/month)" className={`${cls} flex-1`} />
              <input type="text" value={detail.value} onChange={(e) => handleCoreDetailChange(index, 'value', e.target.value)} placeholder="Value (e.g. 3-5 picks)" className={`${cls} flex-1`} />
              <div className="flex flex-col gap-1 flex-shrink-0">
                <button type="button" onClick={() => handleMoveCoreDetail(index, -1)} disabled={index === 0} className="p-1 text-gray-400 hover:text-white disabled:opacity-30">▲</button>
                <button type="button" onClick={() => handleMoveCoreDetail(index, 1)} disabled={index === coreDetails.length - 1} className="p-1 text-gray-400 hover:text-white disabled:opacity-30">▼</button>
              </div>
              <button type="button" onClick={() => handleRemoveCoreDetail(index)} className="p-2 mt-1 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors flex-shrink-0" title="Remove Detail">
                <TrashIcon style={{ width: 16, height: 16 }} />
              </button>
            </div>
          ))}
          <button type="button" onClick={handleAddCoreDetail} className="text-xs flex items-center gap-1 font-medium text-primary-400 hover:text-primary-300 transition-colors mt-2">
            <PlusIcon style={{ width: 12, height: 12 }} /> Add Detail
          </button>
        </div>
      </div>

      {/* Delivery */}
      <div className="border-t border-dark-700 pt-5">
        <p className={sh}>Delivery Channels</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Toggle label="Email" field="email_delivery" />
          <Toggle label="Telegram" field="telegram_delivery" />
          <Toggle label="WhatsApp" field="whatsapp_support" />
          <Toggle label="Priority Support" field="priority_support" />
        </div>
      </div>

      {/* Reports */}
      <div className="border-t border-dark-700 pt-5">
        <p className={sh}>Reports & Analysis</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Toggle label="Monthly Outlook" field="monthly_outlook" />
          <Toggle label="Weekly Sectoral Reports" field="weekly_sectoral_reports" />
          <Toggle label="Custom Research Reports" field="custom_research_reports" />
          <Toggle label="Market Radar Newsletter" field="market_radar_newsletter" />
        </div>
      </div>

      {/* Trading */}
      <div className="border-t border-dark-700 pt-5">
        <p className={sh}>Trading</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Toggle label="Intraday Trades" field="intraday_trades" />
          <Toggle label="Positional Trades" field="positional_trades" />
          <Toggle label="Strategy-Based Options" field="strategy_based_options" />
        </div>
      </div>

      {/* Portfolio */}
      <div className="border-t border-dark-700 pt-5">
        <p className={sh}>Portfolio Services</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Portfolio Tracker</label>
            <select value={form.portfolio_tracker ?? ''} onChange={set('portfolio_tracker')} className={cls}>
              <option value="">None</option>
              <option value="basic">Basic</option>
              <option value="premium">Premium</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Portfolio Audit Frequency</label>
            <select value={form.portfolio_audit_frequency ?? ''} onChange={set('portfolio_audit_frequency')} className={cls}>
              <option value="">None</option>
              <option value="quarterly">Quarterly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Toggle label="Portfolio Rebalancing" field="portfolio_rebalancing" />
          <Toggle label="Risk Management Plan" field="risk_management_plan" />
        </div>
      </div>

      {/* Tools */}
      <div className="border-t border-dark-700 pt-5">
        <p className={sh}>Premium Tools</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Toggle label="Watchlist Access" field="watchlist_access" />
          <Toggle label="Alerts Access" field="alerts_access" />
          <Toggle label="Risk Meter" field="risk_meter_access" />
          <Toggle label="Live Market Access" field="live_market_access" />
        </div>
      </div>

      {/* Support */}
      <div className="border-t border-dark-700 pt-5">
        <p className={sh}>Support</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          {renderField({ label: 'Review Calls', field: 'review_calls', placeholder: 'e.g. Monthly' })}
        </div>
        <Toggle label="Call Support" field="call_support" />
      </div>

      {/* Compliance */}
      <div className="border-t border-dark-700 pt-5">
        <p className={sh}>Compliance & Education</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Toggle label="Compliance Dashboard" field="compliance_dashboard" />
          <Toggle label="Net Worth Certificate" field="net_worth_certificate" />
          <Toggle label="Education Content" field="basic_education_content" />
        </div>
      </div>

      {/* Status */}
      <div className="border-t border-dark-700 pt-5">
        <p className={sh}>Status</p>
        <Toggle label="Plan is Active" field="is_active" />
      </div>

      <div className="flex gap-3 pt-2">
        <button type="submit" disabled={loading}
          className="flex-1 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 disabled:opacity-50 transition-colors">
          {loading ? 'Saving…' : initial?.id ? 'Update Plan' : 'Create Plan'}
        </button>
        <button type="button" onClick={onCancel}
          className="px-5 py-2.5 rounded-xl border border-dark-600 bg-dark-800 text-sm font-medium text-gray-300 hover:bg-dark-700 transition-colors">
          Cancel
        </button>
      </div>
    </form>
  )
}

// ─── Plan Card ─────────────────────────────────────────────────────────────────

const TYPE_BADGE: Record<string, string> = {
  BASIC: 'bg-gray-500/20 text-gray-300',
  STANDARD: 'bg-blue-500/20 text-blue-300',
  PREMIUM: 'bg-amber-500/20 text-amber-300',
  VIP: 'bg-purple-500/20 text-purple-300',
}

function PlanCard({ plan, onEdit, onToggle, onAnalytics }: {
  plan: Plan; onEdit: () => void; onToggle: () => void; onAnalytics: () => void
}) {
  const deliveryOn = [
    plan.email_delivery && 'Email',
    plan.telegram_delivery && 'Telegram',
    plan.whatsapp_support && 'WhatsApp',
    plan.priority_support && 'Priority',
  ].filter(Boolean) as string[]

  const tradingOn = [
    plan.intraday_trades && 'Intraday',
    plan.positional_trades && 'Positional',
    plan.strategy_based_options && 'F&O',
  ].filter(Boolean) as string[]

  return (
    <div className={`bg-dark-800 rounded-2xl border shadow-sm p-5 ${plan.is_active ? (plan.is_popular ? 'border-amber-500/60' : 'border-dark-700') : 'border-dark-700 opacity-55'}`}>
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${TYPE_BADGE[plan.plan_type] || 'bg-gray-500/20 text-gray-300'}`}>
              {plan.plan_type}
            </span>
            {plan.is_popular && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 font-medium flex items-center gap-1">
                <StarSolidIcon style={{ width: 10, height: 10 }} /> Popular
              </span>
            )}
            {!plan.is_active && (
              <span className="text-xs px-2 py-0.5 bg-dark-700 text-gray-500 rounded-full">Inactive</span>
            )}
          </div>
          <h3 className="font-bold text-white truncate">{plan.name}</h3>
          {plan.description && <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{getRichTextPlainText(plan.description)}</p>}
        </div>
        <div className="text-right flex-shrink-0 ml-3">
          {Number(plan.discount_percent) > 0 && (
            <p className="text-xs text-gray-500 line-through">₹{plan.price_min.toLocaleString('en-IN')}</p>
          )}
          <p className="font-bold text-amber-400">
            ₹{Number(plan.final_price ?? plan.price_min).toLocaleString('en-IN')}
          </p>
          {Number(plan.discount_percent) > 0 && (
            <p className="text-xs text-green-400">{Math.round(Number(plan.discount_percent))}% off</p>
          )}
          <p className="text-xs text-gray-400">{plan.duration_days}d</p>
        </div>
      </div>

      <div className="space-y-2 mb-3 text-xs">
        {(() => {
          try {
            if (plan.core_details) {
              const details = JSON.parse(plan.core_details)
              if (Array.isArray(details)) {
                return details.map((d, i) => (
                  <p key={i} className="text-gray-300 flex items-center gap-1.5">
                    <CheckIcon className="text-green-400 flex-shrink-0" style={{ width: 11, height: 11 }} />
                    {d.value}
                  </p>
                ))
              }
            }
          } catch {}
          return null
        })()}
      </div>

      <div className="flex flex-wrap gap-1 mb-4">
        {deliveryOn.map(d => (
          <span key={d} className="text-xs bg-blue-500/10 text-blue-400 px-2 py-0.5 rounded-full">{d}</span>
        ))}
        {tradingOn.map(t => (
          <span key={t} className="text-xs bg-orange-500/10 text-orange-400 px-2 py-0.5 rounded-full">{t}</span>
        ))}
        {plan.portfolio_tracker && (
          <span className="text-xs bg-emerald-500/10 text-emerald-400 px-2 py-0.5 rounded-full capitalize">
            {plan.portfolio_tracker} tracker
          </span>
        )}
        {plan.compliance_dashboard && (
          <span className="text-xs bg-rose-500/10 text-rose-400 px-2 py-0.5 rounded-full">Compliance</span>
        )}
      </div>

      <div className="border-t border-dark-700 pt-3 flex items-center justify-between">
        <button onClick={onAnalytics} className="text-xs text-gray-400 hover:text-amber-400 transition-colors">
          <strong className="text-gray-100">{plan.active_subscribers}</strong> subscriber{plan.active_subscribers !== 1 ? 's' : ''}
          {plan.sort_order !== undefined && plan.sort_order !== null && (
            <span className="ml-2 text-gray-600">#{plan.sort_order}</span>
          )}
        </button>
        <div className="flex gap-1">
          <button onClick={onAnalytics} className="p-1.5 text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors" title="Analytics">
            <ChartBarIcon style={{ width: 16, height: 16 }} />
          </button>
          <button onClick={onEdit} className="p-1.5 text-gray-400 hover:text-amber-400 hover:bg-amber-500/10 rounded-lg transition-colors" title="Edit">
            <PencilSquareIcon style={{ width: 16, height: 16 }} />
          </button>
          <button onClick={onToggle}
            className={`p-1.5 rounded-lg transition-colors ${plan.is_active
              ? 'text-gray-400 hover:text-red-400 hover:bg-red-500/10'
              : 'text-gray-500 hover:text-green-400 hover:bg-green-500/10'}`}
            title={plan.is_active ? 'Deactivate' : 'Reactivate'}
          >
            {plan.is_active ? <TrashIcon style={{ width: 16, height: 16 }} /> : <ArrowPathIcon style={{ width: 16, height: 16 }} />}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── FAQ Management ─────────────────────────────────────────────────────────────

const EMPTY_FAQ = { question: '', answer: '', sort_order: 0, is_active: true }

function FAQManagement() {
  const [faqs, setFaqs] = useState<FAQ[]>([])
  const [loading, setLoading] = useState(true)
  const [editingFaq, setEditingFaq] = useState<FAQ | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [form, setForm] = useState(EMPTY_FAQ)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const fetchFaqs = async () => {
    try {
      const res = await api.get('/admin/faqs')
      setFaqs(res.data)
    } catch {}
    finally { setLoading(false) }
  }

  useEffect(() => { fetchFaqs() }, [])

  const openCreate = () => { setForm(EMPTY_FAQ); setEditingFaq(null); setShowCreate(true); setError('') }
  const openEdit = (f: FAQ) => { setForm({ question: f.question, answer: f.answer, sort_order: f.sort_order, is_active: f.is_active }); setEditingFaq(f); setShowCreate(true); setError('') }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.question.trim() || !form.answer.trim()) { setError('Question and answer are required'); return }
    setSaving(true)
    setError('')
    try {
      if (editingFaq) {
        await api.put(`/admin/faqs/${editingFaq.id}`, form)
      } else {
        await api.post('/admin/faqs', form)
      }
      setShowCreate(false)
      setEditingFaq(null)
      fetchFaqs()
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const deleteFaq = async (id: number) => {
    if (!window.confirm('Delete this FAQ?')) return
    try {
      await api.delete(`/admin/faqs/${id}`)
      fetchFaqs()
    } catch {}
  }

  const toggleFaq = async (f: FAQ) => {
    try {
      await api.put(`/admin/faqs/${f.id}`, { ...f, is_active: !f.is_active })
      fetchFaqs()
    } catch {}
  }

  const cls = "w-full text-sm bg-dark-900 border border-dark-600 text-white placeholder-gray-500 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary-500"

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-400">FAQs shown on the public pricing page. Add, reorder or edit anytime.</p>
        <button onClick={openCreate}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors">
          <PlusIcon style={{ width: 14, height: 14 }} />
          Add FAQ
        </button>
      </div>

      {showCreate && (
        <form onSubmit={handleSave} className="bg-dark-800 border border-dark-600 rounded-xl p-5 space-y-3">
          <h3 className="text-sm font-semibold text-white">{editingFaq ? 'Edit FAQ' : 'New FAQ'}</h3>
          {error && <p className="text-xs text-red-400">{error}</p>}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Question *</label>
            <input type="text" value={form.question} onChange={e => setForm(p => ({ ...p, question: e.target.value }))}
              placeholder="e.g. How does billing work?" className={cls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Answer *</label>
            <textarea rows={3} value={form.answer} onChange={e => setForm(p => ({ ...p, answer: e.target.value }))}
              placeholder="Full answer text…" className={`${cls} resize-none`} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Sort Order</label>
              <input type="number" value={form.sort_order} onChange={e => setForm(p => ({ ...p, sort_order: Number(e.target.value) }))}
                className={cls} />
            </div>
            <div className="flex items-end pb-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <div onClick={() => setForm(p => ({ ...p, is_active: !p.is_active }))}
                  className={`relative flex-shrink-0 rounded-full transition-colors ${form.is_active ? 'bg-amber-500' : 'bg-dark-600'}`}
                  style={{ width: 32, height: 18 }}>
                  <span className="absolute rounded-full bg-white shadow transition-all"
                    style={{ width: 14, height: 14, top: 2, left: form.is_active ? 16 : 2 }} />
                </div>
                <span className="text-xs text-gray-300">Active (visible)</span>
              </label>
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <button type="submit" disabled={saving}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-primary-600 transition-colors">
              {saving ? 'Saving…' : editingFaq ? 'Update' : 'Create'}
            </button>
            <button type="button" onClick={() => { setShowCreate(false); setEditingFaq(null) }}
              className="px-4 py-2 bg-dark-700 text-gray-300 rounded-lg text-sm hover:bg-dark-600 transition-colors">
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="text-center py-8 text-gray-400 text-sm">Loading…</div>
      ) : faqs.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <ChatBubbleLeftEllipsisIcon style={{ width: 32, height: 32 }} className="mx-auto mb-2 opacity-40" />
          <p className="text-sm">No FAQs yet. Add one above.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {faqs.map((faq) => (
            <div key={faq.id} className={`bg-dark-800 border border-dark-700 rounded-xl p-4 ${!faq.is_active ? 'opacity-50' : ''}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs text-gray-500">#{faq.sort_order}</span>
                    {!faq.is_active && <span className="text-xs bg-dark-700 text-gray-500 px-1.5 py-0.5 rounded">Hidden</span>}
                  </div>
                  <p className="text-sm font-medium text-white">{faq.question}</p>
                  <p className="text-xs text-gray-400 mt-1 line-clamp-2">{faq.answer}</p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => openEdit(faq)} className="p-1.5 text-gray-400 hover:text-amber-400 hover:bg-amber-500/10 rounded-lg transition-colors" title="Edit">
                    <PencilSquareIcon style={{ width: 14, height: 14 }} />
                  </button>
                  <button onClick={() => toggleFaq(faq)} className="p-1.5 text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors" title={faq.is_active ? 'Hide' : 'Show'}>
                    <ArrowPathIcon style={{ width: 14, height: 14 }} />
                  </button>
                  <button onClick={() => deleteFaq(faq.id)} className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors" title="Delete">
                    <TrashIcon style={{ width: 14, height: 14 }} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function PlansManagement() {
  const navigate = useNavigate()
  const [tab, setTab] = useState<'plans' | 'faqs'>('plans')
  const [plans, setPlans] = useState<Plan[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<Plan | null>(null)
  const [toggleConfirm, setToggleConfirm] = useState<Plan | null>(null)
  const [billingTab, setBillingTab] = useState<BillingPeriod>('monthly')

  const fetchPlans = async () => {
    setLoading(true)
    try {
      const res = await api.get('/admin/plans')
      setPlans(res.data)
    } catch {}
    finally { setLoading(false) }
  }

  useEffect(() => { fetchPlans() }, [])

  const save = async (data: any) => {
    if (editing) {
      await api.put(`/admin/plans/${editing.id}`, data)
      setEditing(null)
    } else {
      await api.post('/admin/plans', data)
      setShowForm(false)
    }
    fetchPlans()
  }

  const confirmToggle = async () => {
    if (!toggleConfirm) return
    try {
      await api.delete(`/admin/plans/${toggleConfirm.id}`)
      setToggleConfirm(null)
      fetchPlans()
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Action failed')
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <nav className="text-xs text-gray-400 mb-1"><Link to="/admin" className="hover:text-amber-400 transition-colors">RA Desk</Link> &rsaquo; Plans</nav>
          <h1 className="text-2xl font-bold text-white">Plans & FAQs</h1>
          <p className="text-sm text-gray-400 mt-0.5">Manage advisory plans by billing period, pricing, and public FAQ content</p>
        </div>
        {tab === 'plans' && (
          <button onClick={() => { setShowForm(true); setEditing(null) }}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors">
            <PlusIcon style={{ width: 16, height: 16 }} />
            New Plan
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-dark-800 border border-dark-700 rounded-xl p-1 w-fit">
        {(['plans', 'faqs'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium capitalize transition-colors ${tab === t ? 'bg-dark-700 text-white' : 'text-gray-400 hover:text-gray-200'}`}>
            {t === 'plans' ? 'Subscription Plans' : 'FAQ Content'}
          </button>
        ))}
      </div>

      {/* Plans tab */}
      {tab === 'plans' && (
        <>
          {(showForm || editing) && (
            <div className="bg-dark-800 rounded-2xl border border-dark-700 shadow-sm p-6">
              <h3 className="text-base font-bold text-white mb-5">
                {editing ? `Edit — ${editing.name}` : 'New Subscription Plan'}
              </h3>
              <PlanForm
                key={editing ? editing.id : 'new'}
                initial={editing || undefined}
                onSave={save}
                onCancel={() => { setShowForm(false); setEditing(null) }}
              />
            </div>
          )}

          {/* Billing period selector */}
          <div className="flex gap-2 mb-6">
            {BILLING_SECTIONS.map(section => {
              const count = plans.filter(p => p.billing_period === section.key).length
              return (
                <button
                  key={section.key}
                  onClick={() => setBillingTab(section.key)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
                    billingTab === section.key
                      ? 'bg-primary-500 text-white'
                      : 'bg-dark-800 border border-dark-700 text-gray-400 hover:text-gray-200 hover:border-dark-600'
                  }`}
                >
                  {section.label}
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${billingTab === section.key ? 'bg-black/20' : 'bg-dark-700 text-gray-500'}`}>
                    {count}
                  </span>
                </button>
              )
            })}
          </div>

          {loading ? (
            <div className="text-center py-12 text-gray-400">Loading…</div>
          ) : (() => {
            const sectionPlans = plans.filter(p => p.billing_period === billingTab)
            const active = sectionPlans.filter(p => p.is_active)
            const inactive = sectionPlans.filter(p => !p.is_active)
            return sectionPlans.length === 0 ? (
              <div className="text-center py-8 text-gray-500 text-sm bg-dark-800/50 border border-dashed border-dark-700 rounded-2xl">
                No {BILLING_SECTIONS.find(b => b.key === billingTab)?.label.toLowerCase()} plans yet.
              </div>
            ) : (
              <div className="space-y-6">
                {active.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {active.map(plan => (
                      <PlanCard key={plan.id} plan={plan}
                        onEdit={() => { setEditing(plan); setShowForm(false) }}
                        onToggle={() => setToggleConfirm(plan)}
                        onAnalytics={() => navigate(`/admin/plans/${plan.slug}`)} />
                    ))}
                  </div>
                )}
                {inactive.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Inactive</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                      {inactive.map(plan => (
                        <PlanCard key={plan.id} plan={plan}
                          onEdit={() => { setEditing(plan); setShowForm(false) }}
                          onToggle={() => setToggleConfirm(plan)}
                          onAnalytics={() => navigate(`/admin/plans/${plan.slug}`)} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )
          })()}
        </>
      )}

      {/* FAQs tab */}
      {tab === 'faqs' && <FAQManagement />}

      {/* Toggle confirm modal */}
      {toggleConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="bg-dark-800 rounded-2xl shadow-xl border border-dark-700 w-full max-w-sm p-6">
            <h3 className="font-bold text-white mb-2">
              {toggleConfirm.is_active ? 'Deactivate Plan' : 'Reactivate Plan'}
            </h3>
            <p className="text-sm text-gray-400 mb-5">
              {toggleConfirm.is_active
                ? <>Deactivate <strong className="text-white">{toggleConfirm.name}</strong>? Existing subscribers won't be affected, but the plan won't appear in the public pricing page.</>
                : <>Reactivate <strong className="text-white">{toggleConfirm.name}</strong>? It will immediately appear on the public pricing page.</>
              }
            </p>
            <div className="flex gap-3">
              <button onClick={confirmToggle}
                className={`flex-1 py-2.5 rounded-xl text-white text-sm font-medium transition-colors ${toggleConfirm.is_active ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}>
                {toggleConfirm.is_active ? 'Deactivate' : 'Reactivate'}
              </button>
              <button onClick={() => setToggleConfirm(null)}
                className="flex-1 py-2.5 rounded-xl border border-dark-600 bg-dark-800 text-sm font-medium text-gray-300 hover:bg-dark-700 transition-colors">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}
